/**
 * DeveloperContractModal.jsx — emails-ready dev docs auto-generated from
 * /campaigns/whatsapp/preview-contract for the current template + mapping.
 *
 * Tabs: JSON Schema | cURL | Express | FastAPI | Postman.
 * Each tab has a Copy button.
 *
 * Props:
 *   open:             boolean
 *   onClose:          () => void
 *   templateId:       string | number
 *   templatemapping:  { [varKey]: string }
 */

import React, { useEffect, useState } from 'react';
import { Modal, Tabs, Button, Spin, Empty, message } from 'antd';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import audienceSourceApi from '../../api/audienceSource.api';

const tabKeys = ['schema', 'curl', 'express', 'fastapi', 'postman'];

export default function DeveloperContractModal({
  open,
  onClose,
  templateId,
  templatemapping,
}) {
  const [loading, setLoading] = useState(false);
  const [contract, setContract] = useState(null);
  const [activeKey, setActiveKey] = useState('schema');

  const fetchContract = async () => {
    if (!templateId) return;
    setLoading(true);
    try {
      const res = await audienceSourceApi.previewContract({
        templateId,
        templatemapping,
      });
      setContract(res.data?.data || res.data?.entity || res.data);
    } catch (err) {
      message.error(`Could not generate contract: ${err?.response?.data?.message || err.message}`);
      setContract(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId, JSON.stringify(templatemapping || {})]);

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => message.success('Copied'),
      () => message.error('Copy failed')
    );
  };

  const tabItems = tabKeys.map((k) => {
    const labelMap = {
      schema: 'JSON Schema',
      curl: 'cURL',
      express: 'Express (Node)',
      fastapi: 'FastAPI (Python)',
      postman: 'Postman',
    };
    const code =
      contract?.[k] ||
      (k === 'schema' && contract?.json_schema
        ? JSON.stringify(contract.json_schema, null, 2)
        : '');
    return {
      key: k,
      label: labelMap[k],
      children: (
        <div style={{ position: 'relative' }}>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copy(code)}
            style={{ position: 'absolute', top: 6, right: 6, zIndex: 2 }}
          >
            Copy
          </Button>
          {code ? (
            <pre
              style={{
                background: '#1f1f1f',
                color: '#f5f5f5',
                padding: 16,
                borderRadius: 8,
                maxHeight: 480,
                overflow: 'auto',
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              <code>{code}</code>
            </pre>
          ) : (
            <Empty description="Not available for this template/mapping yet" />
          )}
        </div>
      ),
    };
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Developer Contract — share this with your engineer"
      width={820}
      footer={[
        <Button key="reload" icon={<ReloadOutlined />} onClick={fetchContract}>
          Regenerate
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      ) : !contract ? (
        <Empty description="Pick a template and mapping, then reopen to generate" />
      ) : (
        <>
          <p style={{ color: '#6b6b6b', marginBottom: 12 }}>
            This contract was auto-generated from the current template and
            variable mapping. Send it to your developer — they need to expose a
            webhook that returns the JSON shape shown in the Schema tab.
          </p>
          <Tabs
            activeKey={activeKey}
            onChange={setActiveKey}
            items={tabItems}
            destroyInactiveTabPane
          />
        </>
      )}
    </Modal>
  );
}
